import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { getIndustries } from '../data/industriesMultilang';
import { Industry } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

const IndustrySelector: React.FC = () => {
  const { language, t } = useLanguage();
  const industries = getIndustries(language);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    dragFree: true,
    containScroll: false,
    align: 'start',
  });

  // Continuous auto-scroll effect
  useEffect(() => {
    if (!emblaApi) return;

    const autoScroll = () => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    };

    const intervalId = setInterval(autoScroll, 2000); // Scroll every 2 seconds

    return () => clearInterval(intervalId);
  }, [emblaApi]);

  return (
    <section className="py-20 relative overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Background blur elements */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3), transparent)', filter: 'blur(80px)' }}></div>
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full opacity-30" style={{ background: 'radial-gradient(circle, rgba(236, 72, 153, 0.3), transparent)', filter: 'blur(80px)' }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-bold text-center mb-4"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {t.industry.title}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-xl text-center mb-12"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {t.industry.subtitle}
        </motion.p>
      </div>

      {/* Embla Carousel with Auto Scroll */}
      <div className="embla-industry mb-16 overflow-hidden" ref={emblaRef}>
        <div className="embla__container flex gap-8 py-8 px-4">
          {/* Render industries multiple times for seamless loop */}
          {[...industries, ...industries, ...industries].map((industry, index) => (
            <div key={`${industry.id}-${index}`} className="flex-shrink-0" style={{ minWidth: '220px', flex: '0 0 auto' }}>
              <motion.button
                onClick={() => setSelectedIndustry(industry)}
                whileHover={{ scale: 1.08, y: -8 }}
                whileTap={{ scale: 0.95 }}
                className={`group w-full px-12 py-10 rounded-3xl transition-all duration-500 relative overflow-hidden ${
                  selectedIndustry?.id === industry.id
                    ? 'glass-strong'
                    : 'glass'
                }`}
                style={{
                  background: selectedIndustry?.id === industry.id
                    ? 'linear-gradient(135deg, var(--color-primary), var(--color-accent))'
                    : undefined,
                  color: 'var(--color-text-primary)',
                  boxShadow: selectedIndustry?.id === industry.id
                    ? '0 12px 48px rgba(99, 102, 241, 0.5)'
                    : '0 8px 32px rgba(0, 0, 0, 0.2)',
                }}
              >
                {/* Hover gradient effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative z-10">
                  <motion.div
                    className="text-6xl mb-4"
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    {industry.icon}
                  </motion.div>
                  <div className="text-xl font-bold whitespace-nowrap">{industry.name}</div>
                </div>
              </motion.button>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Pain Points */}
        <AnimatePresence mode="wait">
          {selectedIndustry && (
            <motion.div
              key={selectedIndustry.id}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="max-w-4xl mx-auto">
                <h3
                  className="text-2xl font-bold text-center mb-6"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {t.industry.painPointsTitle}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedIndustry.pains.map((pain, index) => (
                    <motion.div
                      key={pain}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="glass px-6 py-4 rounded-lg flex items-center gap-3"
                    >
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: 'var(--color-accent)' }}
                      ></div>
                      <span
                        className="text-lg font-medium"
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {pain}
                      </span>
                    </motion.div>
                  ))}
                </div>
                <p
                  className="text-xl font-semibold text-center mt-8"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {t.industry.soundFamiliar}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default IndustrySelector;
